import { useMutation, useQuery } from "@apollo/client";
import { useFormik } from "formik";
import * as Yup from "yup";

import { GridInputs, Table } from "../../../../../../../components/Functional";
import { Button, getInput, Modal } from "../../../../../../../components/ui";
import ThreeStateCheckbox from "../../../../../../../components/ui/Input/components/ThreeStateCheckbox";
import { CREATE_JOB_ITEM as createJobItemMutation } from "../../../../../../../graphql/mutation/JobItem";
import { FETCH_ARTISTS as fetchArtists } from "../../../../../../../graphql/query/Artist";
import { FETCH_INSTANCES_V2 as fetchInstancesV2 } from "../../../../../../../graphql/query/Instance";
import { useCompanies } from "../../../../../../../hooks/useCompanies";
import { convertToFilter } from "../../../../../../../util";

const FilterBar = props => {
  const { formik } = props || {};
  const { data: { artists = [] } = {} } = useQuery(fetchArtists());
  const { companies } = useCompanies();

  const inputs = {
    className: "grid-cols-5 gap-4",
    inputs: [
      {
        label: "Company",
        name: "companyId",
        type: "multi-select",
        options: companies?.edges?.map(item => ({
          label: item?.name,
          value: item?.id,
        })),
      },
      { label: "Control Reference", name: "ref", type: "text" },
      {
        label: "Artist",
        name: "_edition._artwork._artist.id",
        type: "multi-select",
        options: artists?.map(item => ({
          label: item?.name,
          value: item?.id,
        })),
      },
      { label: "Title", name: "_edition._artwork.title", type: "text" },
      { label: "Edition #", name: "editionNumber", type: "text" },
      { label: "Name", name: "name", type: "text" },
      { label: "Artist Ref", name: "artistRef", type: "text" },
      { label: "Series", name: "_edition._artwork.series", type: "text" },
      {
        designType: "threeStateCheckbox",
        label: "Photographed",
        name: "_edition._artwork.photographed",
        type: "toggle",
      },
    ],
  };

  const inputProps = {
    formik,
    ...inputs,
  };

  return <GridInputs {...inputProps} />;
};

const AddWorkModal = props => {
  const { closeModal, jobId } = props;
  const [createJobItem, { loading, error, reset }] = useMutation(
    createJobItemMutation(),
    { refetchQueries: ["FetchJobItemsV2"] },
  );

  const handleClick = () => {
    const { instanceId } = formik?.values || {};

    createJobItem({
      variables: {
        input: {
          jobId: jobId,
          instanceId,
        },
      },
    }).then(resp => {
      const { data: { createJobItem: { success } = {} } = {} } = resp;

      if (success) {
        resetAll();
      }
    });
  };

  const formik = useFormik({
    initialValues: {},
    onSubmit: handleClick,
  });

  const filterFormik = useFormik({
    initialValues: {},
    validationSchema: Yup.object({
      editionNumber: Yup.number()
        .nullable()
        .integer()
        .positive()
        .typeError("Invalid input"),
    }),
  });

  const toggleProps = props => {
    const { id, name } = props || {};

    return {
      type: "toggle",
      designType: "twoStateCheckbox",
      name,
      ...formik.getFieldProps(name),
      value: formik?.values?.[name] === id,
      onChange: value => {
        formik.getFieldHelpers(name).setValue(value ? id : null);
      },
    };
  };

  const EditionNumber = ({ values }) => {
    if (values?.edition?.number === 1 || !values?.editionNumber) return null;
    return (
      <div className="flex flex-col">
        <span className="text-base font-medium leading-5 text-black">{`${values?.editionNumber}/${values?.edition?.number}`}</span>
      </div>
    );
  };

  const resetAll = () => {
    formik?.resetForm();
    reset?.();
    filterFormik?.resetForm();
    closeModal?.();
  };

  const tableProps = {
    dataKey: "instancesV2",
    FETCH_QUERY: fetchInstancesV2({
      // Instance scalar fields
      ref: true,
      editionNumber: true,
      fabricated: true,
      locationHistory: true,
      // Instance associations
      edition: {
        number: true,
        year: true,
        artwork: {
          title: true,
          medium: true,
          dimensions: true,
          artists: {
            name: true,
          },
          images: {
            imgT: true,
          },
        },
      },
      status: {
        name: true,
      },
    }),
    headers: [
      {
        name: "instanceId",
        type: "custom",
        component: props => getInput(toggleProps(props)),
      },
      { label: "Status", name: "status.name", type: "label" },
      { label: "Ref", name: "ref", type: "label" },
      {
        label: "Artwork Image",
        name: "edition.artwork.images[0].imgT",
        type: "image",
      },
      {
        label: "Artist Name",
        name: "edition.artwork.artists.[0].name",
        type: "label",
      },
      {
        label: "Title",
        name: "edition.artwork.title",
        type: "label",
        widthInPx: 400,
      },
      {
        label: "Edition",
        name: "editionNumber",
        type: "custom",
        component: EditionNumber,
      },
      { label: "Year", name: "edition.year", type: "label" },
      { label: "Medium", name: "edition.artwork.medium", type: "label" },
      {
        label: "Dimensions",
        name: "edition.artwork.dimensions",
        type: "label",
      },
      {
        label: "Fabricated",
        name: "fabricated",
        type: "custom",
        component: props => (
          <ThreeStateCheckbox
            checked={props.values?.fabricated}
            disabled={true}
          />
        ),
      },
      { label: "Location", name: "locationHistory", type: "label" },
    ],
    inputs: [],
    limit: 10,
    skip: () => !filterFormik?.isValid,
    variables: convertToFilter({
      values: filterFormik?.values,
      skipKeys: ["limit", "offset"],
    }),
    pagination: {
      variant: "sm",
    },
    variant: "dynamicHeight",
  };

  if (error) {
    return (
      <div className="mt-6 flex w-full flex-col">
        <div className="flex flex-row">
          <div className="flex flex-1 flex-col">
            <div className="text-2xl font-bold">Error adding work</div>
          </div>
          <div className="flex flex-col">
            <div>
              <Button
                action="default"
                className="mr-4"
                label="Cancel"
                onClick={() => {
                  resetAll();
                }}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="py-6">{error?.message}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 flex w-full flex-col">
      <div className="flex flex-row">
        <div className="flex flex-1 flex-col">
          <div className="text-2xl font-bold">Select Artwork</div>
        </div>
        <div className="mb-5 flex flex-col">
          <div>
            <Button
              action="default"
              className="mr-4"
              label="Cancel"
              onClick={() => {
                resetAll();
              }}
            />
            <Button
              label={loading ? "Adding" : "Add"}
              disabled={loading || !formik?.values?.instanceId}
              onClick={formik?.submitForm}
            />
          </div>
        </div>
      </div>
      <FilterBar formik={filterFormik} />
      <Table {...tableProps} />
    </div>
  );
};

const AddWork = props => {
  const { jobId, statusId } = props || {};

  const modalProps = {
    body: AddWorkModal,
    closeOnBackdrop: false,
    jobId,
    hideCloseButton: true,
    scale: "lg",
  };

  return (
    <Modal {...modalProps}>
      <Button
        label="Add Work"
        action="black"
        disabled={statusId !== 1 && statusId !== null}
      />
    </Modal>
  );
};

export default AddWork;
